import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ModernNavbar from '../component/header/ModernNavbar';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const ModernContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');
  const cardBg = useColorModeValue('white', 'neutral.800');
  const toast = useToast();

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: 'ส่งข้อความสำเร็จ',
        description: 'ขอบคุณสำหรับข้อความของคุณ เราจะติดต่อกลับโดยเร็วที่สุด',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    }, 2000);
  };

  return (
    <Box>
      <ModernNavbar />
      
      {/* Hero Section */}
      <Box
        bgImage="url('/assets/images/singha/floor1/IMG_7810.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        py={20}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(36, 36, 36, 0.5)"
        />
        <Container maxW="1200px" position="relative" zIndex={2}>
          <MotionWrapper
           
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading
                as="h1"
                size="2xl"
                fontFamily="heading"
                fontWeight="normal"
              >
                ติดต่อเรา
              </Heading>
              <Text fontSize="xl" maxW="800px" lineHeight="1.6">
                มีคำถามหรือต้องการข้อมูลเพิ่มเติมเกี่ยวกับพิพิธภัณฑ์สิงห์ท่า? 
                ติดต่อเราได้เลย เรายินดีให้บริการ
              </Text>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
              {/* Contact Form */}
              <Box>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                  mb={8}
                >
                  ส่งข้อความถึงเรา
                </Heading>
                <Box
                  as="form"
                  onSubmit={handleSubmit}
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                >
                  <VStack spacing={6}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
                      <FormControl isRequired>
                        <FormLabel color={textColor}>ชื่อ-นามสกุล</FormLabel>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="กรอกชื่อ-นามสกุล"
                          bg="white"
                          border="1px solid"
                          borderColor="neutral.200"
                          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={textColor}>อีเมล</FormLabel>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="กรอกอีเมล"
                          bg="white"
                          border="1px solid"
                          borderColor="neutral.200"
                          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        />
                      </FormControl>
                    </SimpleGrid>
                    
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
                      <FormControl>
                        <FormLabel color={textColor}>เบอร์โทรศัพท์</FormLabel>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="กรอกเบอร์โทรศัพท์"
                          bg="white"
                          border="1px solid"
                          borderColor="neutral.200"
                          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={textColor}>หัวข้อ</FormLabel>
                        <Select
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="เลือกหัวข้อ"
                          bg="white"
                          border="1px solid"
                          borderColor="neutral.200"
                          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        >
                          <option value="general">คำถามทั่วไป</option>
                          <option value="visit">การเข้าชม</option>
                          <option value="exhibition">นิทรรศการ</option>
                          <option value="education">การศึกษา</option>
                          <option value="other">อื่นๆ</option>
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                    
                    <FormControl isRequired>
                      <FormLabel color={textColor}>ข้อความ</FormLabel>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="กรอกข้อความของคุณ"
                        rows={6}
                        bg="white"
                        border="1px solid"
                        borderColor="neutral.200"
                        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                      />
                    </FormControl>
                    
                    <Button
                      type="submit"
                      size="lg"
                      bg="brand.500"
                      _hover={{ bg: 'brand.600' }}
                      isLoading={isSubmitting}
                      loadingText="กำลังส่ง..."
                      w="100%"
                    >
                      ส่งข้อความ
                    </Button>
                  </VStack>
                </Box>
              </Box>

              {/* Contact Information */}
              <Box>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                  mb={8}
                >
                  ข้อมูลติดต่อ
                </Heading>
                <VStack spacing={8} align="flex-start">
                  <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="lg"
                    boxShadow="md"
                    w="100%"
                  >
                    <Heading as="h3" size="md" mb={4} fontFamily="heading" color="brand.500">
                      ที่อยู่
                    </Heading>
                    <Text color={textColor} lineHeight="1.6">
                      พิพิธภัณฑ์สิงห์ท่า<br />
                      ตำบลสิงห์ท่า<br />
                      อำเภอเมืองยโสธร<br />
                      จังหวัดยโสธร 35000
                    </Text>
                  </Box>
                  
                  <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="lg"
                    boxShadow="md"
                    w="100%"
                  >
                    <Heading as="h3" size="md" mb={4} fontFamily="heading" color="brand.500">
                      เวลาเปิดทำการ
                    </Heading>
                    <Text color={textColor} lineHeight="1.6">
                      วันอังคาร - วันอาทิตย์<br />
                      เวลา 09:00 - 16:00 น.<br />
                      ปิดทำการวันจันทร์
                    </Text>
                  </Box>
                  
                  <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="lg"
                    boxShadow="md"
                    w="100%"
                  >
                    <Heading as="h3" size="md" mb={4} fontFamily="heading" color="brand.500">
                      ติดต่อ
                    </Heading>
                    <VStack spacing={3} align="flex-start">
                      <Text color={textColor}>
                        <strong>โทร:</strong> 045-123456
                      </Text>
                      <Text color={textColor}>
                        <strong>อีเมล:</strong> info@singhathamuseum.com
                      </Text>
                      <Text color={textColor}>
                        <strong>เว็บไซต์:</strong> www.singhathamuseum.com
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </SimpleGrid>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Map Section */}
      <Box py={20} bg="neutral.50">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8}>
              <Heading
                as="h2"
                size="xl"
                fontFamily="heading"
                color="neutral.900"
                textAlign="center"
              >
                ตำแหน่งที่ตั้ง
              </Heading>
              <Box
                w="100%"
                h="400px"
                bg="neutral.200"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="neutral.600"
              >
                <Text fontSize="lg">แผนที่ Google Maps จะแสดงที่นี่</Text>
              </Box>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default ModernContactUs; 